import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Star, User, Package, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import { API_URL } from '../config';

const PrivateDriverEndTrip = () => {
  const location = useLocation();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [customerState, setCustomerState] = useState(null);
  const [tripData, setTripData] = useState(null);
  const [customerData, setCustomerData] = useState(null);

  // Get trip data from location state or URL parameters
  useEffect(() => {
    const getTripData = () => {
      // Try to get trip data from location state first
      if (location.state?.trip) {
        const trip = location.state.trip;
        setTripData(trip);
        fetchCustomerDetails(trip.cust_id);
        return;
      }

      // Try to get from URL parameters
      const urlParams = new URLSearchParams(location.search);
      const tripParam = urlParams.get('trip');
      
      if (tripParam) {
        try {
          const trip = JSON.parse(decodeURIComponent(tripParam));
          setTripData(trip);
          fetchCustomerDetails(trip.cust_id);
        } catch (error) {
          console.error('Error parsing trip data:', error);
        }
      }
    };

    getTripData();
  }, [location]);

  const fetchCustomerDetails = async (customerId) => {
    try {
      const response = await fetch(`${API_URL}/customerdetails/${customerId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const result = await response.json();

      console.log("Customer data received:", result);

      if (result && result.length > 0) {
        const customer = result[0];
        setCustomerData(customer);
      } else {
        console.error("No customer data found");
        setCustomerData({
          username: "Unknown Customer",
          account_type: "customer",
          profilePic: null
        });
      }
    } catch (error) {
      console.error("Error fetching customer details:", error);
      setCustomerData({
        username: "Unknown Customer",
        account_type: "customer",
        profilePic: null
      });
    }
  };

  const handleRatingPress = (value) => {
    setRating(value);
  };

  const checkCustomerState = async () => {
    try {
      const response = await fetch(`${API_URL}/trip/${tripData.trip_id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Error checking customer state:", error);
      return null;
    }
  };

  const updateTripCount = async (driverId) => {
    try {
      const response = await fetch(`${API_URL}/driver/driver_trips_completed_update/${driverId}`);
      const result = await response.json();

      if (result.status === "200") {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error("Error updating trip count:", error);
      return false;
    }
  };

  const endTrip = async () => {
    await updateTripCount(tripData.driver_id);

    console.log("Trip code:", tripData.delivery_received_confirmation_code);
    console.log("Entered code:", code);

    if (tripData.delivery_received_confirmation_code == code) {
      try {
        const response = await fetch(`${API_URL}/trip/end-trip/driver/${tripData.trip_id}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        });
        
        const result = await response.json();
        console.log("End trip result:", result);
        return true;
      } catch (error) {
        console.error("Error ending trip:", error);
        return false;
      }
    } else {
      return false;
    }
  };

  const handleFeedback = async () => {
    if (rating === 0) {
      alert("Please provide a rating.");
      return;
    }
    if (comment.trim() === "") {
      alert("Please provide additional comments.");
      return;
    }

    setSubmitting(true);

    const tripEnded = await endTrip();
    console.log("Trip ended:", tripEnded);

    if (!tripEnded) {
      setSubmitting(false);
      alert("Please Enter the correct Package Code");
      return;
    } else {
      console.log("Checking customer state");
      
      // Poll for customer state
      while (true) {
        setLoading(true);
        
        const state = await checkCustomerState();
        setCustomerState(state);

        if (state && state[0]?.customer_status === "Ended") {
          try {
            const feedbackData = {
              driver_comment: comment,
              customer_stars: rating,
            };

            console.log("Driver submitting feedback:", {
              url: `${API_URL}/trip/driverComment/${tripData.trip_id}`,
              payload: feedbackData,
            });

            const response = await fetch(`${API_URL}/trip/driverComment/${tripData.trip_id}`, {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(feedbackData),
            });

            if (!response.ok) {
              const errorText = await response.text();
              console.error("Driver feedback failed:", errorText);
              throw new Error(`Driver feedback failed (${response.status}): ${errorText}`);
            }

            const result = await response.json();
            console.log("Driver feedback success:", result);

            if (response.ok) {
              alert("Success! " + result.message);
              
              // Navigate back to home
              if (window.ReactNativeWebView) {
                window.ReactNativeWebView.postMessage(JSON.stringify({
                  type: 'NAVIGATE_HOME'
                }));
              } else {
                window.close();
              }
            } else {
              alert("Error: " + (result.message || "Failed to submit feedback."));
            }
          } catch (error) {
            console.error("Error posting feedback:", error);
            alert("An error occurred while submitting your feedback.");
          } finally {
            setLoading(false);
            setSubmitting(false);
          }
          break; // Exit the loop after executing the feedback logic
        }

        // Wait for 3 seconds before the next check
        await new Promise((resolve) => setTimeout(resolve, 3000));
      }
    }
  };

  const goBack = () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: 'GO_BACK'
      }));
    } else {
      window.history.back();
    }
  };

  const getProfileImage = () => {
    if (!customerData?.profilePic || customerData.profilePic === "") {
      return `data:image/svg+xml;base64,${btoa(`
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="30" cy="30" r="30" fill="#f0f0f0"/>
          <circle cx="30" cy="25" r="10" fill="#ccc"/>
          <ellipse cx="30" cy="45" rx="15" ry="10" fill="#ccc"/>
        </svg>
      `)}`;
    }
    
    // In production, this would be the actual image URL
    return customerData.profilePic;
  };

  const renderStars = () => {
    return [1, 2, 3, 4, 5].map((value) => (
      <button
        key={value}
        className={`star-button ${rating >= value ? 'active' : ''}`}
        onClick={() => handleRatingPress(value)}
        style={{
          background: 'none',
          border: 'none',
          fontSize: '24px',
          cursor: 'pointer',
          color: rating >= value ? '#FFD700' : '#ccc',
          padding: '0',
          transition: 'transform 0.2s, color 0.2s',
        }}
        onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
        onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
      >
        ★
      </button>
    ));
  };

  if (!tripData || !customerData) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        flexDirection: 'column',
        backgroundColor: 'white'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #FFC000',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '10px'
        }}></div>
        <div style={{ fontSize: '16px', color: '#666' }}>Loading trip details...</div>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '100%',
      minHeight: '100vh',
      backgroundColor: 'white',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          .star-button:hover {
            transform: scale(1.1);
          }
        `}
      </style>

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        backgroundColor: '#FFC000'
      }}>
        <button
          onClick={goBack}
          style={{
            padding: '8px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '24px',
            color: '#000'
          }}
        >
          ←
        </button>
        <div style={{
          fontSize: '18px',
          fontWeight: 'bold',
          color: '#000',
          flex: 1,
          textAlign: 'center'
        }}>
          End Trip
        </div>
        <div style={{ width: '40px' }}></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ padding: '20px' }}
      >
        {/* Customer Profile */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.3 }}
          style={{
            backgroundColor: '#FFF',
            padding: '20px',
            borderRadius: '12px',
            marginBottom: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <img
              src={getProfileImage()}
              alt="Customer"
              style={{
                width: '60px',
                height: '60px',
                borderRadius: '30px',
                marginRight: '10px',
                objectFit: 'cover',
                border: '2px solid #f0f0f0'
              }}
            />
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '18px',
                fontWeight: 'bold',
                marginBottom: '2px'
              }}>
                {customerData.username}
              </div>
              <div style={{
                fontSize: '16px',
                color: '#555'
              }}>
                {customerData.account_type}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Rating Section */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3, duration: 0.3 }}
          style={{
            backgroundColor: '#FFF',
            padding: '20px',
            borderRadius: '12px',
            marginBottom: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#333',
            marginBottom: '15px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Star size={18} style={{ marginRight: '8px', color: '#FFC000' }} />
            Rate Your Experience
          </h3>
          
          <div style={{
            fontSize: '16px',
            color: '#666',
            marginBottom: '10px'
          }}>
            How was your customer?
          </div>
          
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '15px',
            gap: '2px'
          }}>
            {renderStars()}
          </div>
        </motion.div>

        {/* Comment Section */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4, duration: 0.3 }}
          style={{
            backgroundColor: '#FFF',
            padding: '20px',
            borderRadius: '12px',
            marginBottom: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#333',
            marginBottom: '15px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <User size={18} style={{ marginRight: '8px', color: '#FFC000' }} />
            Additional Comments
          </h3>
          
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Share your feedback"
            rows={4}
            style={{
              width: '100%',
              border: '1px solid #ccc',
              borderRadius: '6px',
              padding: '10px',
              fontSize: '14px',
              fontFamily: 'inherit',
              resize: 'vertical',
              backgroundColor: '#fff'
            }}
          />
        </motion.div>

        {/* Package Code (for deliveries) */}
        {tripData.weight !== 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.3 }}
            style={{
              backgroundColor: '#FFF',
              padding: '20px',
              borderRadius: '12px',
              marginBottom: '20px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#333',
              marginBottom: '15px',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Package size={18} style={{ marginRight: '8px', color: '#FFC000' }} />
              Package Code
            </h3>
            
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Enter Package code"
              style={{
                width: '100%',
                border: '1px solid #ccc',
                borderRadius: '6px',
                padding: '10px',
                fontSize: '14px',
                fontFamily: 'inherit',
                backgroundColor: '#fff'
              }}
            />
          </motion.div>
        )}

        {/* Submit Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.3 }}
        >
          <button
            onClick={handleFeedback}
            disabled={submitting}
            style={{
              width: '100%',
              padding: '16px',
              borderRadius: '12px',
              border: 'none',
              fontSize: '16px',
              fontWeight: 'bold',
              textAlign: 'center',
              cursor: submitting ? 'not-allowed' : 'pointer',
              backgroundColor: submitting ? '#ccc' : '#FFC000',
              color: '#000',
              transition: 'opacity 0.2s',
              opacity: submitting ? 0.6 : 1,
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {submitting ? 'Processing...' : 'Submit Feedback'}
          </button>
        </motion.div>
      </motion.div>

      {/* Loading Modal */}
      {loading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            width: '200px',
            padding: '20px',
            borderRadius: '10px',
            backgroundColor: 'white',
            textAlign: 'center'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #0000ff',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 10px'
            }}></div>
            <div style={{
              fontSize: '18px',
              fontWeight: 'bold',
              lineHeight: '1.4'
            }}>
              Waiting for
            </div>
            <div style={{
              fontSize: '18px',
              fontWeight: 'bold',
              lineHeight: '1.4'
            }}>
              Customer
            </div>
            <div style={{
              fontSize: '18px',
              fontWeight: 'bold',
              lineHeight: '1.4'
            }}>
              To End trip...
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivateDriverEndTrip;
